import { useMutation, useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Filter } from '../../../../@types/filter';
import { locationApi } from '../../../../apis';
import { CreateLocationDto, Location, UpdateLocationDto } from '../../../../apis/client-axios';
import ButtonWrap from '../../../../components/ButtonWrap';
import InputWrap from '../../../../components/InputWrap';
import ModalWrap from '../../../../components/ModalWrap';
import SvgAdd from '../../../../components/svg/SvgAdd';
import SvgDelete from '../../../../components/svg/SvgDelete';
import SvgEdit from '../../../../components/svg/SvgEdit';
import TableWrap from '../../../../components/TableWrap';
import ToastError from '../../../../components/Toast/ToastError';
import ToastSuccess from '../../../../components/Toast/ToastSuccess';
import Widget from '../../../../components/Widget';
import { LOCATION_LIST } from '../../../../constants/QueryKey';
import './style.scss';

const LocationList = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<Location | null>(null);
  const [filter, setFilter] = useState<Filter>({ page: 1, pageSize: 20, fullTextSearch: '' });

  const {
    data: dataLocationList,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [LOCATION_LIST, filter],
    queryFn: () => {
      return locationApi.locationControllerGetAll(
        filter.page,
        filter.pageSize,
        undefined,
        filter.fullTextSearch,
        undefined,
      );
    },
  });

  const addMutation = useMutation({
    mutationFn: (payload: CreateLocationDto) => locationApi.locationControllerCreate(payload),
    onSuccess: () => {
      setOpenAddModal(false);
      refetch();
      form.resetFields();
      ToastSuccess({
        content: intl.formatMessage({ id: 'locationList.addSuccees' }),
      });
    },
    onError: (error: { response: { data: { message: string; errorCode: string }; status: number } }) => {
      if (error.response.data.errorCode.includes('EXISTED')) {
        ToastError({
          content: intl.formatMessage({ id: 'locationList.beenCreated' }),
        });
      }
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLocationDto }) =>
      locationApi.locationControllerUpdate(id, payload),
    onSuccess: () => {
      setOpenEditModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'locationList.editSuccees' }),
      });
    },
    onError: (error: { response: { data: { message: string; errorCode: string }; status: number } }) => {
      if (error.response.data.errorCode.includes('EXISTED')) {
        ToastError({
          content: intl.formatMessage({ id: 'locationList.beenCreated' }),
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLocationDto }) =>
      locationApi.locationControllerDelete(id, payload),
    onSuccess: () => {
      setFilter((prevFilter) => ({
        ...prevFilter,
        page: 1,
      }));
      setOpenDeleteModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'locationList.deleteSuccees' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'locationList.deleteFail' }),
      });
    },
  });

  const handleAddLocation = () => {
    form
      .validateFields()
      .then((values) => {
        const payload: CreateLocationDto = {
          name: form.getFieldValue('addLocation'),
        };
        addMutation.mutate(payload);
      })
      .catch((error) => {
        console.error('Validate Failed:', error);
      });
  };

  const handleEditLocation = () => {
    form
      .validateFields()
      .then((values) => {
        const payload: UpdateLocationDto = {
          name: form.getFieldValue('editLocation'),
        };
        editMutation.mutate({ id: selectedRowData?.id as string, payload });
      })
      .catch((error) => {
        console.error('Validate Failed:', error);
      });
  };

  const handleDeleteLocation = () => {
    const payload: UpdateLocationDto = {
      name: form.getFieldValue('deleteLocation'),
    };
    deleteMutation.mutate({ id: selectedRowData?.id as string, payload });
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'locationList.title' }),
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => {
        return (
          <div className="location-list-content-name">
            <p className="location-list-content-name-tool">{record?.name}</p>
          </div>
        );
      },
    },
    {
      title: '',
      key: 'action',
      render: (record, row) => (
        <div className="location-list-content-data-options">
          <ButtonWrap
            className="location-list-content-button-edit"
            onClick={() => {
              setOpenEditModal(true);
              setSelectedRowData(row);
              form.setFieldValue('editLocation', row?.name);
            }}
          >
            <SvgEdit /> {intl.formatMessage({ id: 'locationList.editButton' })}
          </ButtonWrap>
          <ButtonWrap
            className="location-list-content-button-delete"
            onClick={() => {
              setOpenDeleteModal(true);
              setSelectedRowData(row);
            }}
          >
            <SvgDelete />
            {intl.formatMessage({ id: 'locationList.deleteButton' })}
          </ButtonWrap>
        </div>
      ),
    },
  ];
  return (
    <div className="location-list">
      <div className="location-list-content">
        <Widget
          breadcrumbs={[
            intl.formatMessage({ id: 'adminMenu.locationManagement' }),
            intl.formatMessage({ id: 'locationList.title' }),
          ]}
          content={
            <TableWrap
              isPagination
              loading={isLoading}
              onSearch={(search) => {
                setFilter((prevFilters) => ({
                  ...prevFilters,
                  page: 1,
                  fullTextSearch: search,
                }));
              }}
              paginationProps={{
                current: dataLocationList?.data?.data?.page || 1,
                pageSize: dataLocationList?.data?.data?.size || 20,
                total: dataLocationList?.data?.data?.total || 0,
                onChange: (page, pageSize) =>
                  setFilter((prevFilters) => ({
                    ...prevFilters,
                    page,
                    pageSize,
                  })),
              }}
              inputProps={{
                placeholder: intl.formatMessage({ id: 'locationList.searchBox' }),
              }}
              tableProps={{
                dataSource: Array.isArray(dataLocationList?.data?.data?.content)
                  ? dataLocationList?.data?.data?.content
                  : [],
                columns,
                className: 'location-list-content-table',
              }}
              rightTopContent={
                <>
                  <ButtonWrap
                    onClick={() => {
                      setOpenAddModal(true);
                    }}
                    variant="outlined"
                    className="location-list-content-button-add"
                  >
                    <SvgAdd /> {intl.formatMessage({ id: 'locationList.addButton' })}
                  </ButtonWrap>
                </>
              }
            />
          }
          title={intl.formatMessage({ id: 'locationList.title' })}
        />
      </div>
      <div className="location-list-content-modal">
        {openAddModal && (
          <ModalWrap
            open={openAddModal}
            cancelText={intl.formatMessage({ id: 'locationList.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'locationList.addButton' })}
            buttonSubmitProps={{ disabled: addMutation.isPending }}
            onSubmit={handleAddLocation}
            onCancel={() => {
              setOpenAddModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={intl.formatMessage({ id: 'locationList.addButtonTitle' })}
            className="location-list-content-modal-add"
            body={
              <Form form={form} layout="vertical">
                <p className="location-list-content-title">
                  {intl.formatMessage({ id: 'adminMenu.locationManagement' })}
                  <span className="label_required">*</span>
                </p>
                <Form.Item
                  name="addLocation"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'common.required' }),
                    },
                    {
                      whitespace: true,
                      message: intl.formatMessage({ id: 'locationList.invalid' }),
                    },
                  ]}
                >
                  <InputWrap
                    className="location-list-content-modal-add-input"
                    placeholder={intl.formatMessage({ id: 'locationList.inputPlaceholder' })}
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
              </Form>
            }
          />
        )}
      </div>
      <div className="location-list-content-modal">
        {openEditModal && (
          <ModalWrap
            open={openEditModal}
            cancelText={intl.formatMessage({ id: 'locationList.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'locationList.editButton' })}
            buttonSubmitProps={{ disabled: editMutation.isPending }}
            onSubmit={handleEditLocation}
            onCancel={() => {
              setOpenEditModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={intl.formatMessage({ id: 'locationList.editButtonTitle' })}
            className="location-list-content-modal-add"
            body={
              <Form form={form} layout="vertical">
                <p className="location-list-content-title">
                  {intl.formatMessage({ id: 'adminMenu.locationManagement' })}
                  <span className="label_required">*</span>
                </p>
                <Form.Item
                  name="editLocation"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'common.required' }),
                    },
                    {
                      whitespace: true,
                      message: intl.formatMessage({ id: 'locationList.invalid' }),
                    },
                  ]}
                >
                  <InputWrap
                    className="location-list-content-modal-add-input"
                    placeholder={intl.formatMessage({ id: 'locationList.inputPlaceholder' })}
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
              </Form>
            }
          />
        )}
      </div>
      <div className="location-list-content-modal">
        {openDeleteModal && (
          <ModalWrap
            open={openDeleteModal}
            cancelText={intl.formatMessage({ id: 'locationList.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'locationList.deleteButton' })}
            buttonSubmitProps={{ disabled: deleteMutation.isPending }}
            onSubmit={handleDeleteLocation}
            onCancel={() => setOpenDeleteModal(false)}
            type="upsert"
            title={intl.formatMessage({ id: 'locationList.deleteButtonTitle' })}
            className="location-list-content-modal-delete"
            body={
              <div className="location-list-content-modal-delete-body">
                <p className="location-list-content-modal-delete-title">
                  <span className="location-list-content-name-tool name">{selectedRowData?.name}</span>
                  {intl.formatMessage({ id: 'booking.modal.delete' })}
                </p>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default LocationList;
